// Serverfehler in einen lesbaren deutschen Satz uebersetzen.
//
// Der Grund fuer diese Datei: FastAPI liefert `detail` in DREI Formen, und
// `String(detail)` trifft nur eine davon.
//
//   HTTPException(403, "Postfach nicht freigeschaltet")  -> String
//   HTTPException(422, {"error": "invalid_attendees"})   -> Objekt
//   Pydantic-Schemafehler                                -> ARRAY von Objekten
//
// `String({…})` und `String([{…}])` ergeben beide `[object Object]`. Damit war
// bis hierher JEDE 422-Meldung der Anwendung unsichtbar — und zwar zunehmend,
// weil jede neue Schema-Pruefung einen weiteren solchen Pfad schafft. Drei
// Stellen hatten sich deshalb schon eigene Auffangfunktionen gebaut
// (`fuSaveFehlerText` in Mail.tsx, `bannerFehlerText` in Settings.tsx, die
// Code-Auswertung in CalendarMs.tsx). Diese Datei ist die gemeinsame Antwort.
//
// Getrennt von `api.ts`, damit sie ohne Browser/Vite pruefbar ist:
//   node --experimental-strip-types frontend/api-fehlertext.test.mjs
//
// BEWUSST NICHT hier drin: eine Uebersetzungstabelle fuer Feldnamen. Sie waere
// fuer den halben Datenbestand zu pflegen und sofort veraltet. Der Feldpfad
// wird technisch, aber ehrlich genannt; der deutsche Rahmen kommt aus dem
// Satzbau. Die Meldungen der EIGENEN Validatoren (`Value error, …`) sind
// ohnehin schon deutsch — genau die sieht der Nutzer am haeufigsten.

/** Pydantic haengt die Herkunft vorne an (`["body","config","cc",1]`). Fuer den
 *  Nutzer ist das Rauschen: er hat kein „body" ausgefuellt, sondern ein Feld. */
const HERKUNFT = new Set(["body", "query", "path", "header", "cookie"]);

/** Ab hier wird zusammengefasst statt aufgezaehlt. Vier Fehler sind eine
 *  Information, zwanzig sind eine Wand. Die Restzahl wird GENANNT — still
 *  verschlucken waere genau der Fehler, den diese Datei behebt. */
const MAX_EINTRAEGE = 3;

/** Laenge, ab der ein beanstandeter Wert gekuerzt wird. Ein 4-kB-HTML-Rumpf im
 *  Toast hilft niemandem, die ersten Zeichen identifizieren ihn aber. */
const MAX_WERT = 60;

function kuerzen(s: string, max = MAX_WERT): string {
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** `["body","config","cc",1]` -> `config.cc[1]` */
export function feldPfad(loc: unknown): string {
  if (!Array.isArray(loc)) return "";
  const teile = [...loc];
  // Nur das FUEHRENDE Herkunfts-Segment faellt weg. Ein Feld, das selbst
  // „body" heisst (verschachtelte Nutzlast), bleibt sichtbar.
  if (teile.length > 1 && typeof teile[0] === "string" && HERKUNFT.has(teile[0])) {
    teile.shift();
  }
  let out = "";
  for (const t of teile) {
    if (typeof t === "number") out += `[${t}]`;
    else if (typeof t === "string") out += out ? `.${t}` : t;
  }
  return out;
}

/** Der beanstandete Wert, so wie ihn der Nutzer wiedererkennt. `undefined`
 *  ergibt `null` = weglassen: bei „Feld fehlt" gibt es keinen Wert zu zeigen,
 *  und „(Wert: undefined)" ist Rauschen. */
export function wertText(input: unknown): string | null {
  if (input === undefined) return null;
  if (input === null) return "leer";
  if (typeof input === "string") return input === "" ? "leer" : `„${kuerzen(input)}"`;
  if (typeof input === "number" || typeof input === "boolean") return String(input);
  try {
    return kuerzen(JSON.stringify(input) ?? String(input));
  } catch {
    return null;
  }
}

/** Ein Pydantic-Eintrag -> eine Zeile. `null`, wenn nichts Belastbares drinsteht. */
function pydanticZeile(eintrag: unknown): string | null {
  if (!eintrag || typeof eintrag !== "object") return null;
  const e = eintrag as { loc?: unknown; msg?: unknown; input?: unknown };

  // Pydantic v2 stellt den Meldungen eigener Validatoren „Value error, " voran.
  // Das ist Werkzeug-Innenleben; dahinter steht der Satz, der gemeint war.
  const grund = kuerzen(
    String(e.msg ?? "").replace(/^(Value|Assertion) error,\s*/i, "").trim(),
    140,
  );
  const pfad = feldPfad(e.loc);
  const wert = "input" in e ? wertText(e.input) : null;

  if (!pfad && !grund) return null;

  const kopf = pfad ? `Feld „${pfad}": ${grund || "ungültiger Wert"}` : grund;
  return wert === null ? kopf : `${kopf} (Wert: ${wert})`;
}

/** Aus einer Pydantic-Fehlerliste einen Satz bauen. */
function ausListe(detail: unknown[], fallback: string): string {
  const zeilen = detail
    .map(pydanticZeile)
    .filter((z): z is string => Boolean(z));
  if (!zeilen.length) return fallback;

  const gezeigt = zeilen.slice(0, MAX_EINTRAEGE);
  const rest = zeilen.length - gezeigt.length;
  const satz = gezeigt.join(" · ");
  return rest > 0
    ? `${satz} · … und ${rest} weitere${rest === 1 ? "r Fehler" : " Fehler"}`
    : satz;
}

/** Kandidaten fuer „das menschenlesbare Feld" eines Handler-Objekts, in der
 *  Reihenfolge abnehmender Lesbarkeit: `message` ist ein Satz, `error` meist
 *  ein Code (`invalid_attendees`) — beides schlaegt `[object Object]`. */
const TEXTFELDER = ["message", "error", "detail", "msg", "reason"] as const;

function ausObjekt(detail: Record<string, unknown>, fallback: string): string {
  for (const f of TEXTFELDER) {
    const v = detail[f];
    if (typeof v === "string" && v.trim()) return kuerzen(v.trim(), 300);
  }
  return fallback;
}

/**
 * `detail` -> Anzeigetext.
 *
 * Vertrag (die Regressions-Zusage steht an erster Stelle):
 *  - String  -> UNVERAENDERT. Alle bestehenden Meldungen bleiben wortgleich.
 *  - Array   -> Pydantic: Feld + Grund + beanstandeter Wert, mehrere verbunden.
 *  - Objekt  -> das menschenlesbare Feld; gibt es keins, lieber `HTTP <status>`.
 *  - sonst   -> `HTTP <status>`.
 */
export function fehlertextAusDetail(detail: unknown, status: number): string {
  const fallback = `HTTP ${status}`;
  // Wortgleich zum bisherigen `String(detail)` — und zwar bewusst inklusive
  // des leeren Strings: hier wird kein Verhalten „nebenbei mitverbessert".
  if (typeof detail === "string") return detail;
  if (typeof detail === "number" || typeof detail === "boolean") return String(detail);
  if (Array.isArray(detail)) return ausListe(detail, fallback);
  if (detail && typeof detail === "object") {
    return ausObjekt(detail as Record<string, unknown>, fallback);
  }
  return fallback;
}

/**
 * Anzeigetext aus dem geparsten Antwort-Rumpf. Ersetzt an beiden Stellen in
 * `api.ts` den `String(body.detail)`-Ausdruck; die Vorpruefung ist wortgleich
 * uebernommen (`typeof body === "object" && body && "detail" in body` — das
 * `&& body` faengt `null` ab, dessen `typeof` ebenfalls `"object"` ist).
 */
export function fehlertextAusBody(body: unknown, status: number): string {
  if (typeof body === "object" && body && "detail" in body) {
    return fehlertextAusDetail((body as { detail: unknown }).detail, status);
  }
  return `HTTP ${status}`;
}
